import {
  BarChartOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
} from 'antd'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { FormattedDate } from '@/components/common/FormattedDate'
import { TruncatedId } from '@/components/common/TruncatedId'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import type { PaginatedItems } from '@/types/api'

import { EditPromoCodeModal } from './components/EditPromoCodeModal'

type PromoCodeDto = components['schemas']['PromoCodeDto']
type PromoCodeStatsDto = components['schemas']['PromoCodeStatsDto']
type PromoCodeRedemptionDto = components['schemas']['PromoCodeRedemptionDto']

export default function PromoCodeShowPage() {
  const { id: promoCodeId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'
  const [editOpen, setEditOpen] = useState(false)

  const promoQuery = useCustom<PromoCodeDto>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/promo_codes/${promoCodeId}`,
    method: 'get',
  })

  const promo = promoQuery.query.data?.data as PromoCodeDto | undefined

  if (!promoCodeId) return null

  if (promoQuery.query.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/promo-codes">Promo Codes</Link> },
          { title: promo?.code ?? promoCodeId },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Promo Code: <Tag color="blue">{promo?.code ?? '—'}</Tag>
        </h2>
        <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
          Edit
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Info',
            icon: <InfoCircleOutlined />,
            children: <PromoInfoTab promo={promo} />,
          },
          {
            key: 'redemptions',
            label: 'Redemptions',
            icon: <UsergroupAddOutlined />,
            children: <RedemptionsTab promoCodeId={promoCodeId} />,
          },
          {
            key: 'stats',
            label: 'Stats',
            icon: <BarChartOutlined />,
            children: <StatsTab promoCodeId={promoCodeId} />,
          },
        ]}
      />

      <EditPromoCodeModal
        open={editOpen}
        promoCode={promo}
        onClose={() => setEditOpen(false)}
        onUpdated={() => void promoQuery.query.refetch()}
      />
    </div>
  )
}

function PromoInfoTab({ promo }: { promo: PromoCodeDto | undefined }) {
  const creatorQuery = useCustom<components['schemas']['AdminUserDto']>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${promo?.createdBy ?? ''}`,
    method: 'get',
    queryOptions: { enabled: Boolean(promo?.createdBy) },
  })

  const creatorEmail = (
    creatorQuery.query.data?.data as
      | components['schemas']['AdminUserDto']
      | undefined
  )?.email

  if (!promo) return null

  return (
    <Card>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="ID">{promo.id}</Descriptions.Item>
        <Descriptions.Item label="Code">
          <Tag color="blue">{promo.code}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Amount">
          ${Number(promo.amount).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Active">
          {promo.isActive ? (
            <Tag color="green">Yes</Tag>
          ) : (
            <Tag color="red">No</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Max Usages">
          {promo.maxTotalUsages}
        </Descriptions.Item>
        <Descriptions.Item label="Free Credits Duration">
          {promo.freeCreditsDurationDays} days
        </Descriptions.Item>
        <Descriptions.Item label="Valid From">
          <FormattedDate date={promo.validFrom} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Valid To">
          <FormattedDate date={promo.validTo} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          <FormattedDate date={promo.createdAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Updated">
          <FormattedDate date={promo.updatedAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Created By">
          {creatorEmail ?? <TruncatedId id={promo.createdBy} />}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}

function RedemptionsTab({ promoCodeId }: { promoCodeId: string }) {
  const redemptionsQuery = useCustom<PaginatedItems<PromoCodeRedemptionDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/promo_codes/${promoCodeId}/redemptions`,
    method: 'get',
    config: { query: { page: 1, perPage: 50 } },
  })

  const redemptions =
    (
      redemptionsQuery.query.data?.data as
        | PaginatedItems<PromoCodeRedemptionDto>
        | undefined
    )?.items ?? []

  return (
    <Table
      loading={redemptionsQuery.query.isLoading}
      dataSource={redemptions}
      rowKey="id"
      columns={[
        { title: 'Email', dataIndex: 'email', key: 'email' },
        {
          title: 'User',
          dataIndex: 'userId',
          key: 'userId',
          width: 120,
          render: (id: string) => <TruncatedId id={id} />,
        },
        {
          title: 'Amount',
          dataIndex: 'amount',
          key: 'amount',
          width: 100,
          render: (v: string) => `$${Number(v).toFixed(2)}`,
        },
        {
          title: 'Redeemed At',
          dataIndex: 'redeemedAt',
          key: 'redeemedAt',
          width: 180,
          render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
        },
      ]}
      pagination={{ pageSize: 10 }}
    />
  )
}

function StatsTab({ promoCodeId }: { promoCodeId: string }) {
  const statsQuery = useCustom<PromoCodeStatsDto>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/promo_codes/${promoCodeId}/stats`,
    method: 'get',
  })

  const stats = statsQuery.query.data?.data as PromoCodeStatsDto | undefined

  if (statsQuery.query.isLoading) return <Spin />

  if (!stats) return <div>No stats available</div>

  return (
    <Row gutter={[16, 16]}>
      <Col xs={12} sm={8} lg={6}>
        <Card>
          <Statistic title="Redemptions" value={stats.redemptionCount} />
        </Card>
      </Col>
      <Col xs={12} sm={8} lg={6}>
        <Card>
          <Statistic title="Remaining Usages" value={stats.remainingUsages} />
        </Card>
      </Col>
      <Col xs={12} sm={8} lg={6}>
        <Card>
          <Statistic
            title="Total Redeemed"
            value={Number(stats.totalRedeemedAmount).toFixed(2)}
            prefix="$"
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} lg={6}>
        <Card>
          <Statistic
            title="Currently Valid"
            value={stats.isCurrentlyValid ? 'Yes' : 'No'}
            valueStyle={{
              color: stats.isCurrentlyValid ? '#52c41a' : '#ff4d4f',
            }}
          />
        </Card>
      </Col>
    </Row>
  )
}
